import { Mail, Phone, MapPin, Instagram, Facebook } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { TikTokIcon, WhatsAppIcon } from "@/components/icons/BrandIcons";
import { SOCIAL_LINKS } from "@/lib/social";

export default function Footer() {
  const { t } = useLang();
  const { content } = useContent();
  const company = content?.company || { name: "Eazy-Travels", email: "info@eazy-travels.com", phone: "+40 72 546 3917", address: "Bucharest, Romania" };

  return (
    <footer className="border-t border-white/5 bg-[#070707] relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <Link to="/" className="inline-flex items-center mb-5">
              <img
                src="/logo.png"
                alt="Eazy-Travels"
                className="h-12 w-auto object-contain"
              />
            </Link>
            <p className="text-white/60 text-sm leading-relaxed max-w-xs">
              {company.tagline || "Your gateway to Europe and beyond — premium visa, relocation and study services."}
            </p>
          </div>

          <div>
            <h4 className="overline mb-5">{t("footer.quick")}</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/" className="text-white/70 hover:text-[var(--gold)] transition">{t("nav.home")}</Link></li>
              <li><Link to="/services" className="text-white/70 hover:text-[var(--gold)] transition">{t("nav.services")}</Link></li>
              <li><Link to="/about" className="text-white/70 hover:text-[var(--gold)] transition">{t("nav.about")}</Link></li>
              <li><Link to="/blog" className="text-white/70 hover:text-[var(--gold)] transition">{t("nav.blog")}</Link></li>
              <li><Link to="/contact" className="text-white/70 hover:text-[var(--gold)] transition">{t("nav.contact")}</Link></li>
              <li><Link to="/documents" className="text-white/70 hover:text-[var(--gold)] transition" data-testid="footer-documents-link">Upload Documents</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="overline mb-5">{t("footer.contact")}</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2.5 text-white/70">
                <Mail size={16} className="text-[var(--gold)] mt-0.5 shrink-0" />
                <a href={`mailto:${company.email}`} className="hover:text-[var(--gold)] break-all" data-testid="footer-email">{company.email}</a>
              </li>
              <li className="flex items-start gap-2.5 text-white/70">
                <Phone size={16} className="text-[var(--gold)] mt-0.5 shrink-0" />
                <span>{company.phone}</span>
              </li>
              <li className="flex items-start gap-2.5 text-white/70">
                <MapPin size={16} className="text-[var(--gold)] mt-0.5 shrink-0" />
                <span>{company.address}</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="overline mb-5">{t("footer.followus")}</h4>
            <div className="flex gap-3">
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 grid place-items-center rounded-full border border-white/10 text-white/70 hover:text-[var(--gold)] hover:border-[var(--gold)]/40 transition">
                <Instagram size={17} />
              </a>
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 grid place-items-center rounded-full border border-white/10 text-white/70 hover:text-[var(--gold)] hover:border-[var(--gold)]/40 transition">
                <Facebook size={17} />
              </a>
              <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="w-10 h-10 grid place-items-center rounded-full border border-white/10 text-white/70 hover:text-[var(--gold)] hover:border-[var(--gold)]/40 transition">
                <TikTokIcon size={17} />
              </a>
              <a href={SOCIAL_LINKS.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-10 h-10 grid place-items-center rounded-full border border-white/10 text-white/70 hover:text-[var(--gold)] hover:border-[var(--gold)]/40 transition">
                <WhatsAppIcon size={17} />
              </a>
            </div>
            <div className="mt-6 flex items-center gap-4">
              <Link
                to="/portal/login"
                className="text-xs text-white/40 hover:text-[var(--gold)] transition"
                data-testid="client-portal-footer-link"
              >
                Client Portal
              </Link>
              <Link
                to="/admin/login"
                className="text-xs text-white/40 hover:text-[var(--gold)] transition"
                data-testid="admin-login-footer-link"
              >
                Admin
              </Link>
            </div>
          </div>
        </div>

        <div className="gold-divider my-12" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/40">
          <p>© {new Date().getFullYear()} {company.name}. {t("footer.rights")}</p>
          <p>Crafted with care for travelers worldwide.</p>
        </div>
      </div>
    </footer>
  );
}
